import { Store, AnyAction } from 'redux';
import { Actions as EditorActions } from 'actions/editorActions';
import { addUndoState } from 'actions/editorActions';

const ReduxFormActions = require('redux-form').actionTypes;

const undoableActions = [
    EditorActions.ADD_NEW_CONTAINER,
    EditorActions.ADD_NEW_ASSET,
    EditorActions.SET_ACTIVE_ASSET,
    EditorActions.SET_ACTIVE_CONTAINER,
    EditorActions.UPDATE_ASSET,
    EditorActions.UPDATE_CONTAINER,
    EditorActions.DELETE_ASSET,
    EditorActions.DELETE_CONTAINER,
    ReduxFormActions.CHANGE,
].reduce((o, a) => Object.assign(o, { [a]: a }), {});

const collapsibleActions = [
    EditorActions.UPDATE_CONTAINER,
    EditorActions.UPDATE_ASSET,
    ReduxFormActions.CHANGE,
].reduce((o, a) => Object.assign(o, { [a]: a }), {});

const isReduxFormAction = (action: AnyAction) => {
    return /redux-form\//ig.test(action.type);
};

const isEditorFormAction = (action: AnyAction) => {
    if (isReduxFormAction(action)) {
        const { form } = action.meta;
        // True if it's an asset form or the container-properties form
        if (form.indexOf('-asset-') > 0 || form === 'container-properties') {
            return true;
        }
    }
    return false;
};

const isCollapsible = (action: AnyAction) => {
    if (typeof action.type === 'string' && collapsibleActions[action.type]) {
        if (isReduxFormAction(action)) {
            return isEditorFormAction(action);
        }
        else {
            return true;
        }
    }
    return false;
};

// An action is undoable if it is in the undoable dictionary
// (and if it's a form field change, the form has to be an editor form)
const isUndoable = (action: AnyAction) => {
    if (typeof action.type === 'string' && undoableActions[action.type]) {
        if (action.type === ReduxFormActions.CHANGE) {
            return isEditorFormAction(action);
        }
        else {
            return true;
        }
    }
    return false;
};

//
// Would have preferred to implement this through sagas except that sagas run *after*
// the actions have fired but undo/redo needs to capture state before an action runs.
//
export const watchForUndoableEditorActions = (/* store: Store<any> */) => (next: (a: AnyAction) => unknown) => (action: AnyAction) => {
    // If an action is undoable then capture the current state before the reducers run
    // on the undoable action. We'll do this by inserting another action that causes the
    // editorReducer to save state. And then we allow the original action to be processed.
    if (isUndoable(action)) {
        // Also, allow this action to be collapsed with the next action if applicable
        // (e.g. if the user is typing into a field, we can collapse individual changes)
        const mergeForward = isCollapsible(action);

        const modifiedAction = Object.assign({}, action, {
            meta: Object.assign({}, action.meta, { mergeForward }),
        });
        next(addUndoState(modifiedAction));
    }

    return next(action);
};
